import React, { FC, useEffect, useMemo } from 'react'
import {
  getParentDepartment,
  useLuxLabel,
  useLuxMeasure,
} from '@moonpig/web-core-utils'
import {
  PageTypeEnum,
  ProductModalProvider,
} from '@moonpig/web-shared-products'
import { useLoggedIn } from '@moonpig/web-core-auth'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { Search } from '../../components/Search'
import { useSearchStore } from '../../store/SearchStore'
import { FilterSelectionError } from '../../components/FilterError/FilterErrorMessage'
import { useLuxAddExperimentData } from '../../utils'
import { useFindLocaleText } from '../../text-localisation'
import { SelectedSuggestion } from '../../components/types'
import { StateIntegerFilter } from '../../store/slices/filters/types'
import { usePageContextFacets } from '../../store/helpers'

export const SearchWrapper: FC<{
  luxLabel: string
  useClientSideRouting: boolean
  experiments?: { [key: string]: string } | undefined
  pageTitle: string
  pageType: PageTypeEnum
  groupCardProject?: string
  department: DepartmentsEnum[]
  searchTerm: string
  selectedSuggestion?: SelectedSuggestion
}> = ({
  luxLabel,
  useClientSideRouting,
  experiments,
  pageTitle,
  pageType,
  groupCardProject,
  department,
  searchTerm,
  selectedSuggestion,
}) => {
  const localiseText = useFindLocaleText()
  const { loggedIn } = useLoggedIn()

  const integerFilters = useSearchStore(store => store.integerFilters)
  const selectedFilters = useSearchStore(store => store.selectedFilters)
  const searchCriteria = useSearchStore(store => store.searchCriteria)
  const resetFilteringEvent = useSearchStore(store => store.resetFilteringEvent)
  const results = useSearchStore(store => store.results)
  const resultsLoading = useSearchStore(store => store.loading)
  const hasFilterSelectionError = useSearchStore(
    store => store.filterSelectionError,
  )
  const clearFilterError = useSearchStore(store => store.clearFilterError)
  const pageContextFacets = usePageContextFacets()

  const filterAppliedIntegerFilters = integerFilters.filter(
    (intFilter: StateIntegerFilter) => {
      return intFilter.value && intFilter.value > 0
    },
  )

  useLuxLabel(luxLabel)
  useLuxMeasure(useClientSideRouting)
  const luxAbTestCustomData = [
    ...Object.entries(experiments ?? {}).map(([name, value]) => ({
      name: `experiment_${name}`,
      value,
    })),
    {
      name: 'search_department',
      value: department.length
        ? getParentDepartment(department[0])
        : 'ALL_CARDS',
    },
    {
      name: 'search_product_count',
      value: results.totalCount.toString(),
    },
    {
      name: 'search_applied_filters',
      value: selectedFilters.length.toString(),
    },
    {
      name: 'user_is_authenticated',
      value: loggedIn.toString(),
    },
  ]
  useLuxAddExperimentData(luxAbTestCustomData)

  useEffect(() => {
    resetFilteringEvent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedSuggestion])

  const productModalContext = useMemo(
    () => ({
      location: pageTitle,
      pageType: PageTypeEnum.SEARCH,
      integerFilters: filterAppliedIntegerFilters.map(f => ({
        name: f.integerId,
        min: f.value || /* istanbul ignore next */ 0,
        max: f.value || /* istanbul ignore next */ 0,
      })),
      filters: pageContextFacets,
      searchCriteria,
    }),
    [pageTitle, filterAppliedIntegerFilters, pageContextFacets, searchCriteria],
  )

  return (
    <ProductModalProvider context={productModalContext}>
      {hasFilterSelectionError && (
        <FilterSelectionError onDismiss={clearFilterError} />
      )}

      <Search
        products={results.products}
        experiments={experiments}
        suggestions={results.suggestions}
        metaTitle={
          pageType === PageTypeEnum.SEARCH
            ? localiseText('find.search_results')
            : pageTitle
        }
        groupCardProject={groupCardProject}
        pageType={pageType}
        totalClickRankDocumentCount={results.totalClickRankDocumentCount}
        loading={!!resultsLoading}
        totalCount={results.totalCount}
        department={department[0]}
      />
    </ProductModalProvider>
  )
}
